import React, { useEffect, useState } from 'react';
import {
  Container, Row, Col, Card, Table, Button, Alert, Spinner, Form,
  Modal, Badge,
} from 'react-bootstrap';
import axios from '../utils/api';

const emptyForm = {
  code: '',
  discount_percent: '',
  start_date: '',
  end_date: '',
  role: '',
  is_active: true,
};

const ROLE_OPTIONS = [
  { value: '', label: 'All roles (unrestricted)' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'inventory_manager', label: 'Inventory Manager' },
  { value: 'administrator', label: 'Administrator' },
  { value: 'super_user', label: 'Super User' },
];

const SuperUserPromoManager = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  const [showRedemptionsModal, setShowRedemptionsModal] = useState(false);
  const [redemptionData, setRedemptionData] = useState(null);
  const [redemptionLoading, setRedemptionLoading] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchPromos = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/promo-codes');
      setPromos(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPromos(); }, []);

  const openCreate = () => {
    setEditingPromo(null);
    setForm(emptyForm);
    setFormError('');
    setShowFormModal(true);
  };

  const openEdit = (promo) => {
    setEditingPromo(promo);
    setForm({
      code: promo.code,
      discount_percent: promo.discount_percent,
      start_date: promo.start_date,
      end_date: promo.end_date,
      role: promo.role || '',
      is_active: promo.is_active,
    });
    setFormError('');
    setShowFormModal(true);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormError('');
  };

  const handleFormSave = async () => {
    if (!editingPromo && !form.code.trim()) { setFormError('Code is required'); return; }
    if (!form.discount_percent || Number(form.discount_percent) <= 0 || Number(form.discount_percent) >= 100) {
      setFormError('Discount must be between 0 and 100 (exclusive)');
      return;
    }
    if (!form.start_date || !form.end_date) { setFormError('Start date and end date are required'); return; }
    if (form.end_date < form.start_date) { setFormError('End date must be on or after start date'); return; }

    try {
      setFormSaving(true);
      setFormError('');
      if (editingPromo) {
        await axios.put(`/api/promo-codes/${editingPromo.id}`, {
          discount_percent: Number(form.discount_percent),
          start_date: form.start_date,
          end_date: form.end_date,
          role: form.role || null,
          is_active: form.is_active,
        });
      } else {
        await axios.post('/api/promo-codes', {
          code: form.code.trim().toUpperCase(),
          discount_percent: Number(form.discount_percent),
          start_date: form.start_date,
          end_date: form.end_date,
          role: form.role || null,
          is_active: form.is_active,
        });
      }
      setShowFormModal(false);
      await fetchPromos();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to save promo code');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await axios.delete(`/api/promo-codes/${deleteTarget.id}`);
      setDeleteTarget(null);
      await fetchPromos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete promo code');
      setDeleteTarget(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const viewRedemptions = async (promo) => {
    try {
      setRedemptionData(null);
      setRedemptionLoading(true);
      setShowRedemptionsModal(true);
      const res = await axios.get(`/api/promo-codes/${promo.id}/redemptions`);
      setRedemptionData(res.data);
    } catch (err) {
      setRedemptionData({ error: err.response?.data?.detail || 'Failed to load redemptions' });
    } finally {
      setRedemptionLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const getStatusBadge = (promo) => {
    if (!promo.is_active) return <Badge bg="secondary">Inactive</Badge>;
    if (promo.end_date < today) return <Badge bg="danger">Expired</Badge>;
    if (promo.start_date > today) return <Badge bg="warning" text="dark">Upcoming</Badge>;
    return <Badge bg="success">Active</Badge>;
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h1>Promo Codes</h1>
            <Button variant="primary" onClick={openCreate}>+ New Promo Code</Button>
          </div>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-4"><Spinner animation="border" /></div>
          ) : promos.length === 0 ? (
            <div className="text-center p-4 text-muted">No promo codes yet. Create one above.</div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Code</th>
                    <th>Discount</th>
                    <th>Role</th>
                    <th>Valid From</th>
                    <th>Valid To</th>
                    <th>Status</th>
                    <th>Redemptions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promos.map((promo) => (
                    <tr key={promo.id}>
                      <td><code className="fw-bold">{promo.code}</code></td>
                      <td className="text-success fw-semibold">{promo.discount_percent}%</td>
                      <td>
                        {promo.role
                          ? <Badge bg="info" text="dark">{ROLE_OPTIONS.find(r => r.value === promo.role)?.label || promo.role}</Badge>
                          : <span className="text-muted">All roles</span>}
                      </td>
                      <td>{promo.start_date}</td>
                      <td>{promo.end_date}</td>
                      <td>{getStatusBadge(promo)}</td>
                      <td>
                        <Button variant="link" size="sm" className="p-0" onClick={() => viewRedemptions(promo)}>
                          {promo.redemption_count} uses
                        </Button>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button variant="outline-primary" size="sm" onClick={() => openEdit(promo)}>Edit</Button>
                          <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(promo)}>Delete</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create / Edit Modal */}
      <Modal show={showFormModal} onHide={() => setShowFormModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingPromo ? 'Edit Promo Code' : 'New Promo Code'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formError && <Alert variant="danger" className="py-2">{formError}</Alert>}
          {!editingPromo && (
            <Form.Group className="mb-3" controlId="promoCode">
              <Form.Label>Code <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. SAVE10"
                value={form.code}
                onChange={(e) => handleFormChange('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                maxLength={20}
              />
              <Form.Text className="text-muted">3–20 uppercase alphanumeric characters</Form.Text>
            </Form.Group>
          )}
          <Form.Group className="mb-3" controlId="promoDiscount">
            <Form.Label>Discount % <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="number"
              min="0.01"
              max="99.99"
              step="0.01"
              placeholder="e.g. 10"
              value={form.discount_percent}
              onChange={(e) => handleFormChange('discount_percent', e.target.value)}
            />
          </Form.Group>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="promoStart">
                <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={form.start_date}
                  onChange={(e) => handleFormChange('start_date', e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="promoEnd">
                <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date"
                  value={form.end_date}
                  onChange={(e) => handleFormChange('end_date', e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3" controlId="promoRole">
            <Form.Label>Applicable Role</Form.Label>
            <Form.Select
              value={form.role}
              onChange={(e) => handleFormChange('role', e.target.value)}
            >
              {ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Restrict this code to a specific account type. Leave as "All roles" for no restriction.
            </Form.Text>
          </Form.Group>
          <Form.Check
            type="switch"
            id="promoActive"
            label="Active"
            checked={form.is_active}
            onChange={(e) => handleFormChange('is_active', e.target.checked)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFormModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleFormSave} disabled={formSaving}>
            {formSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Delete Promo Code</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{deleteTarget?.code}</strong>?
          {deleteTarget?.redemption_count > 0 && (
            <Alert variant="warning" className="mt-2 py-2 mb-0">
              This code has {deleteTarget.redemption_count} redemption(s). Deleting it will remove all redemption history.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Redemptions Modal */}
      <Modal show={showRedemptionsModal} onHide={() => setShowRedemptionsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Redemptions
            {redemptionData?.promo_code && (
              <span className="ms-2 text-muted fs-6">— {redemptionData.promo_code.code} ({redemptionData.promo_code.discount_percent}% off)</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {redemptionLoading ? (
            <div className="text-center py-3"><Spinner animation="border" /></div>
          ) : redemptionData?.error ? (
            <Alert variant="danger">{redemptionData.error}</Alert>
          ) : redemptionData?.redemptions?.length === 0 ? (
            <p className="text-muted text-center">No redemptions yet.</p>
          ) : (
            <div className="table-responsive">
              <Table size="sm" hover>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Buyer</th>
                    <th>Email</th>
                    <th>Order ID</th>
                    <th>Redeemed At</th>
                  </tr>
                </thead>
                <tbody>
                  {(redemptionData?.redemptions || []).map((r, idx) => (
                    <tr key={r.id}>
                      <td>{idx + 1}</td>
                      <td>{r.user_name || '-'}</td>
                      <td>{r.user_email || '-'}</td>
                      <td>
                        {r.order_id ? (
                          <code style={{ fontSize: '0.8em' }}>{r.order_id.slice(0, 8)}…</code>
                        ) : '-'}
                      </td>
                      <td>{r.redeemed_at ? new Date(r.redeemed_at).toLocaleString('en-IN') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRedemptionsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SuperUserPromoManager;
